import { useCallback, useState } from "react";
import { Formik, Field, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import {
  EMPTY_STRING,
  INDEX,
  EMPTY_ARRAY,
  ZERO,
  ONE,
  TWO,
} from "../../../../constants";
import { setSelectedDrawer } from "../../Drawer";
import {
  selectSelectedDataModelId,
  createEndpoint,
  selectEndpoints,
  selectServices,
  fetchEndpoints,
  fetchServices,
  selectCollections,
  createService,
} from "../../../../pages/DataModels";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import Modal, { setSelectedModal } from "../../Modal";
import { actions as datamodelActions } from "../../../../pages/DataModels/dataModels.reducers";
import { DRAWERS } from "../../AppDrawers/drawers.constants";
import { capitalizeFirstLetterOfWord } from "../../../../helpers";
import endpointTemplates from "../../../../assets/templates/endpointWizardTemplate.json";

const { CREATE_ENDPOINT, CREATE_SERVICE } = SPINNER_NAMES;
const { MANAGE_ENDPOINT_DRAWER } = DRAWERS;
const defaultValues = {
  collection: EMPTY_STRING,
  methods: EMPTY_ARRAY,
  authenticate: EMPTY_ARRAY,
};
const { FIRST, SECOND, THIRD } = INDEX;
const MAX_ENDPOINT_NAME_LENGTH = 21;
const { setEndpointFilters } = datamodelActions;
const ENDPOINT = "endpoint";
const COLLECTION_STRING = "%[collectionName]%";
const AUTHENTICATION_STRING = `"%[shouldAuthenticate]%"`;
const CRUD = "crud";
const FORM_TITLE = {
  heading: "How would you like to Proceed?",
  subHeading: "What endpoint you want to create?",
  crud: [
    "Select a collection",
    "Select service",
    "Select services, which needs to be protected.",
  ],
};

const EndpointTemplateModal = () => {
  const dispatch = useDispatch();
  const dataModelId = useSelector(selectSelectedDataModelId);
  const allEndpoints = useSelector(selectEndpoints);
  const allServices = useSelector(selectServices);
  const allCollections = useSelector(selectCollections);

  const allServiceNames = allServices.map(({ name }) => name);

  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [currentStep, setCurrentStep] = useState();
  const [isAdvanceTemplateSelected, setIsAdvancedTemplateSelected] =
    useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(false);

  const doesCollectionExists = allCollections?.data.some(
    ({ isRuleTable }) => !isRuleTable
  );
  const { methods: endpointMethods, serviceTemplates } = endpointTemplates.crud;

  const handleClose = useCallback(() => {
    dispatch(setSelectedModal(null));
  }, [dispatch]);

  const handleCancelButton = () => {
    if (!selectedTemplate || !isAdvanceTemplateSelected) {
      handleClose();
      return;
    }
    if (currentStep === FIRST) {
      setSelectedTemplate();
      setIsAdvancedTemplateSelected();
    } else setCurrentStep(currentStep - ONE);
  };

  const checkIfEndpointExists = (updatedURL, selectedMethod) =>
    allEndpoints?.some(
      ({ method, url }) => method === selectedMethod && url === updatedURL
    );

  const checkIfServiceExists = (serviceName) =>
    allServiceNames.includes(serviceName);

  const getUniqueEndpointUrl = (templateURL, method, collectionName) => {
    let url = templateURL.replaceAll(
      COLLECTION_STRING,
      collectionName.toLowerCase()
    );
    if (!checkIfEndpointExists(url, method)) return url;

    let counter = ONE;
    let uniqueUrl = url;
    while (checkIfEndpointExists(uniqueUrl, method, collectionName)) {
      const index = uniqueUrl.indexOf("/:");
      if (index !== -1)
        uniqueUrl = url.slice(ZERO, index) + `_${counter}` + url.slice(index);
      else uniqueUrl = `${url}_${counter}`;
      counter++;
    }

    return uniqueUrl;
  };

  const getUniqueTemplateName = (name) => {
    let updatedName = name.slice(ZERO, MAX_ENDPOINT_NAME_LENGTH);
    if (checkIfServiceExists(updatedName))
      updatedName = name.slice(ZERO, MAX_ENDPOINT_NAME_LENGTH - TWO);
    let uniqueName = updatedName;
    let counter = ONE;
    while (checkIfServiceExists(uniqueName)) {
      uniqueName = `${updatedName}_${counter}`;
      counter++;
    }
    allServiceNames.push(uniqueName);
    return uniqueName;
  };

  const createCrudEndpoints = (values) => {
    const { collection: selectedCollection, methods, authenticate } = values;

    const apiPromises = methods.map((methodLabel) => {
      const selectedMethod = endpointMethods.find(
        ({ label }) => label === methodLabel
      );
      const { method, key, url: templateURL } = selectedMethod;
      const url = getUniqueEndpointUrl(templateURL, method, selectedCollection);

      const name = `${capitalizeFirstLetterOfWord(
        method.toLowerCase()
      )} ${selectedCollection}`;
      const updatedName = getUniqueTemplateName(name);
      const shouldAuthenticate = !!authenticate?.includes(methodLabel);

      let nodes = serviceTemplates[key].nodes;
      nodes = JSON.parse(
        JSON.stringify(nodes)
          .replaceAll(COLLECTION_STRING, selectedCollection)
          .replaceAll(AUTHENTICATION_STRING, shouldAuthenticate)
      );
      const edges = serviceTemplates[key].edges;
      const request = {
        name: updatedName,
        description: EMPTY_STRING,
        dataModelId,
      };

      return dispatch(
        createService({
          ...request,
          serviceType: ENDPOINT,
          isValid: true,
          flow: { nodes, edges },
        })
      ).then((serviceRes) => {
        if (serviceRes.payload) {
          const serviceDetails = serviceRes.payload;
          const serviceId = serviceDetails.id;
          return dispatch(
            createEndpoint({ ...request, method, serviceId, url })
          ).then((endpointRes) => {
            if (!endpointRes.payload) return Promise.reject();
          });
        }
        return Promise.reject();
      });
    });

    Promise.all(apiPromises).then(() => {
      dispatch(fetchEndpoints({ id: dataModelId }));
      dispatch(fetchServices({ id: dataModelId }));
      dispatch(setEndpointFilters());
      handleClose();
    });
  };

  const handleSubmitButton = (values) => {
    if (currentStep !== SECOND) {
      setCurrentStep((currentStep) => currentStep + ONE);
      return;
    }
    selectedTemplate === CRUD && createCrudEndpoints(values);
  };

  const getFormTitle = () => {
    if (selectedTemplate) return FORM_TITLE[selectedTemplate][currentStep];
    if (isAdvanceTemplateSelected) return FORM_TITLE.subHeading;
    return FORM_TITLE.heading;
  };

  return (
    <Formik initialValues={defaultValues}>
      {({ values, handleReset }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn-primary-light mr-1"
              type="button"
              onClick={handleCancelButton}
            >
              {!selectedTemplate || !isAdvanceTemplateSelected
                ? "Cancel"
                : "Previous"}
            </button>
            {selectedTemplate && (
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => handleSubmitButton(values)}
                disabled={
                  isSpinnerActive || currentStep === SECOND
                    ? isEmpty(values.methods)
                    : isEmpty(values.collection)
                }
              >
                <Spinner
                  name={[CREATE_ENDPOINT, CREATE_SERVICE]}
                  setIsSpinnerActive={setIsSpinnerActive}
                  persistSize
                >
                  {currentStep === THIRD ? "Create" : "Next"}
                </Spinner>
              </button>
            )}
          </div>
        );
        return (
          <Form>
            <Modal
              title="Create Endpoint"
              handleReset={handleReset}
              handleClose={handleClose}
              footer={footer}
            >
              <div className="form-group scrollbar max-h-[400px] flex-1 overflow-auto">
                <div className="text-gray-50 p-4 flex flex-col gap-2">
                  <div>{getFormTitle()}</div>
                  {!isAdvanceTemplateSelected && (
                    <div className="text-center flex flex-col gap-1">
                      <div className="group relative">
                        <button
                          className="btn border border-gray-700 bg-gray-900 hover:bg-gray-800 disabled:hover:bg-gray-900 p-2 flex justify-center w-full"
                          onClick={() => setIsAdvancedTemplateSelected(true)}
                        >
                           Wizard
                        </button>
                      </div>
                      <button
                        className="btn border border-gray-700 bg-gray-900 hover:bg-gray-800 p-2"
                        onClick={() => {
                          dispatch(setSelectedDrawer(MANAGE_ENDPOINT_DRAWER));
                          handleClose();
                        }}
                      >
                        Start from scratch
                      </button>
                    </div>
                  )}
                  {isAdvanceTemplateSelected && !selectedTemplate && (
                    <div className="text-center flex flex-col gap-1">
                      {Object.values(endpointTemplates).map(
                        ({ key, label, enabled }) => enabled && (
                          <div className="group relative" key={key}>
                            <button
                              className="btn border border-gray-700 bg-gray-900 hover:bg-gray-800 disabled:hover:bg-gray-900 p-2 flex justify-center w-full"
                              onClick={() => {
                                setSelectedTemplate(key);
                                setCurrentStep(FIRST);
                              }}
                              disabled={
                                !enabled ||
                                (key === CRUD && !doesCollectionExists)
                              }
                            >
                              {label}
                            </button>
                            {!doesCollectionExists && key === CRUD && (
                              <div className="scale-0 group-hover:scale-100 absolute -bottom-8 right-2 border border-gray-600 opacity-100 rounded bg-gray-800 px-5 py-1 text-gray-50 z-10">
                                No collection available
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  )}
                  {selectedTemplate === CRUD && (
                    <div>
                      {currentStep === FIRST && (
                        <div className="flex flex-col gap-1 px-2">
                          {allCollections?.data.map(
                            ({ id, refName, name, isRuleTable }) =>
                              !isRuleTable && (
                                <label key={id} className="w-fit px-2">
                                  <Field
                                    type="radio"
                                    name="collection"
                                    value={refName}
                                    className="focus:ring-offset-[-1px]
                            disabled:cursor-not-allowed disabled:opacity-70 mr-2"
                                  />
                                  {name}
                                </label>
                              )
                          )}
                        </div>
                      )}
                      {currentStep === SECOND && (
                        <div className="flex flex-col gap-1 px-2">
                          {endpointMethods.map(({ key, label }) => (
                            <label key={key} className="w-fit px-2">
                              <Field
                                type="checkbox"
                                name="methods"
                                value={label}
                                className="rounded-sm focus:ring-offset-[-1px]
                            disabled:cursor-not-allowed disabled:opacity-70 mr-2"
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EndpointTemplateModal;
